// Google Analytics Tracking ID - Add your GA4 Measurement ID here
// Format: G-XXXXXXXXXX
const GA_TRACKING_ID = "G-QEFPP8PV4H";

export const CONSENT_KEY = "cookie-consent";

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  advertising: boolean;
}

/**
 * Loads the Google Analytics script
 * Internal function - don't call directly
 */
const loadGoogleAnalyticsScript = () => {
  if (!GA_TRACKING_ID) {
    console.warn("Google Analytics Tracking ID not configured");
    return;
  }

  // Check if script is already loaded
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
    return;
  }

  // Load the script asynchronously
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
  document.head.appendChild(script);
  
  // Configure GA after script element is created
  window.dataLayer = window.dataLayer || [];
  function gtag(..._: any[]): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
  (window as any).gtag = gtag;
  
  gtag("js", new Date());
  gtag("config", GA_TRACKING_ID);
};

/**
 * Initialize Google Consent Mode v2
 * Call this immediately on app load, before any user interaction
 */
export const initializeConsent = () => {
  window.dataLayer = window.dataLayer || [];
  function gtag(..._: any[]): void {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
  (window as any).gtag = gtag;
  
  // Set default consent to denied (GDPR compliant)
  gtag('consent', 'default', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'wait_for_update': 500, // Wait 500ms for consent banner interaction
  });
  
  // Load GA script (it will respect consent settings)
  loadGoogleAnalyticsScript();
};

/**
 * Update consent when user makes a choice
 * Call this after user accepts/rejects cookies
 */
export const updateConsent = (preferences: CookiePreferences) => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': preferences.analytics ? 'granted' : 'denied',
      'ad_storage': preferences.advertising ? 'granted' : 'denied',
      'ad_user_data': preferences.advertising ? 'granted' : 'denied',
      'ad_personalization': preferences.advertising ? 'granted' : 'denied',
    });
  }
};

/**
 * Check if user has given consent for analytics cookies
 */
export const hasAnalyticsConsent = (): boolean => {
  try {
    const consentString = localStorage.getItem(CONSENT_KEY);
    if (!consentString) return false;
    
    const consent = JSON.parse(consentString) as CookiePreferences;
    return consent.analytics === true;
  } catch (e) {
    // Handle old consent format
    return localStorage.getItem(CONSENT_KEY) === "accepted";
  }
};

/**
 * Get current consent preferences
 */
export const getConsentPreferences = (): CookiePreferences | null => {
  try {
    const consentString = localStorage.getItem(CONSENT_KEY);
    if (!consentString) return null;
    
    return JSON.parse(consentString) as CookiePreferences;
  } catch (e) {
    // Handle old consent format
    const oldConsent = localStorage.getItem(CONSENT_KEY) === "accepted";
    return {
      necessary: true,
      analytics: oldConsent,
      advertising: oldConsent,
    };
  }
};

/**
 * Clear consent (useful for testing or allowing user to reset)
 */
export const clearConsent = () => {
  localStorage.removeItem(CONSENT_KEY);
};

/* ------------------------------------------------------------------ */
/*  Custom event tracking                                              */
/* ------------------------------------------------------------------ */

/**
 * Send a custom GA4 event. Safe to call before GA loads or without
 * analytics consent — it will be silently dropped by Consent Mode.
 *
 * Use snake_case event names per GA4 convention.
 */
export const trackEvent = (
  eventName: string,
  params: Record<string, string | number | boolean | undefined> = {},
) => {
  // Strip undefined values so GA reports stay clean
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined),
  );

  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, clean);
  }
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag?: (...args: any[]) => void;
  }
}
